/**
 * Cross-System Overview Component
 *
 * Visual overview showing all systems and their connections across questions:
 * - System nodes with role indicators
 * - Connection lines between systems
 * - Summary statistics
 * - Navigation to specific canvases
 */

import React, { useMemo } from 'react';
import {
  Database,
  Link2,
  Star,
  Users,
  BookOpen,
  Table2,
  Columns3,
  ArrowRight,
  ChevronRight,
  CheckCircle2,
  Clock
} from 'lucide-react';
import { Badge } from '../../ui/Badge';
import Button from '../../ui/Button';
import type {
  QuestionDataCanvas,
  SystemDataSource,
  SystemRole
} from '../../../stores/analysisStore';

interface CrossSystemOverviewProps {
  canvases: QuestionDataCanvas[];
  onCanvasClick?: (canvasId: string) => void;
  onSystemClick?: (systemId: string) => void;
}

interface SystemSummary {
  systemId: string;
  systemName: string;
  vendor: string;
  functionalArea?: string;
  roles: Set<SystemRole>;
  questionCount: number;
  tableCount: number;
  columnCount: number;
  connectionCount: number;
  canvasIds: string[];
}

interface ConnectionSummary {
  sourceSystemId: string;
  targetSystemId: string;
  connectionCount: number;
  types: Set<string>;
}

const getRoleIcon = (role: SystemRole) => {
  switch (role) {
    case 'primary':
      return <Star className="w-3 h-3 text-blue-500" />;
    case 'supporting':
      return <Users className="w-3 h-3 text-purple-500" />;
    case 'reference':
      return <BookOpen className="w-3 h-3 text-gray-500" />;
  }
};

const getRoleBadge = (role: SystemRole) => {
  switch (role) {
    case 'primary':
      return <Badge variant="default" className="bg-blue-600 text-xs">Primary</Badge>;
    case 'supporting':
      return <Badge variant="outline" className="border-purple-500 text-purple-700 text-xs">Supporting</Badge>;
    case 'reference':
      return <Badge variant="outline" className="border-gray-500 text-gray-700 text-xs">Reference</Badge>;
  }
};

export const CrossSystemOverview: React.FC<CrossSystemOverviewProps> = ({
  canvases,
  onCanvasClick,
  onSystemClick
}) => {
  // Aggregate system data across all canvases
  const { systems, connections, stats } = useMemo(() => {
    const systemMap = new Map<string, SystemSummary>();
    const connectionMap = new Map<string, ConnectionSummary>();

    canvases.forEach(canvas => {
      // Process data sources
      canvas.data_sources.forEach(ds => {
        const existing = systemMap.get(ds.system_id);
        if (existing) {
          existing.roles.add(ds.role);
          existing.questionCount++;
          existing.tableCount += ds.tables.length;
          existing.columnCount += ds.tables.reduce(
            (sum, t) => sum + (t.columns.must?.length || 0) + (t.columns.should?.length || 0) + (t.columns.could?.length || 0),
            0
          );
          existing.canvasIds.push(canvas.canvas_id);
        } else {
          systemMap.set(ds.system_id, {
            systemId: ds.system_id,
            systemName: ds.system_name,
            vendor: ds.vendor,
            functionalArea: ds.functional_area,
            roles: new Set([ds.role]),
            questionCount: 1,
            tableCount: ds.tables.length,
            columnCount: ds.tables.reduce(
              (sum, t) => sum + (t.columns.must?.length || 0) + (t.columns.should?.length || 0) + (t.columns.could?.length || 0),
              0
            ),
            connectionCount: 0,
            canvasIds: [canvas.canvas_id]
          });
        }
      });

      // Process connections
      canvas.connection_points.forEach(cp => {
        const key = `${cp.source_system_id}|${cp.target_system_id}`;
        const existing = connectionMap.get(key);
        if (existing) {
          existing.connectionCount++;
          existing.types.add(cp.connection_type);
        } else {
          connectionMap.set(key, {
            sourceSystemId: cp.source_system_id,
            targetSystemId: cp.target_system_id,
            connectionCount: 1,
            types: new Set([cp.connection_type])
          });
        }

        // Update connection counts on systems
        const sourceSystem = systemMap.get(cp.source_system_id);
        if (sourceSystem) sourceSystem.connectionCount++;
        const targetSystem = systemMap.get(cp.target_system_id);
        if (targetSystem) targetSystem.connectionCount++;
      });
    });

    // Calculate stats
    const totalSystems = systemMap.size;
    const totalConnections = Array.from(connectionMap.values()).reduce((sum, c) => sum + c.connectionCount, 0);
    const approvedCanvases = canvases.filter(c => c.approved).length;
    const primarySystems = Array.from(systemMap.values()).filter(s => s.roles.has('primary')).length;

    return {
      systems: Array.from(systemMap.values()),
      connections: Array.from(connectionMap.values()),
      stats: {
        totalSystems,
        totalConnections,
        totalCanvases: canvases.length,
        approvedCanvases,
        primarySystems
      }
    };
  }, [canvases]);

  // Group systems by functional area
  const systemsByArea = useMemo(() => {
    const grouped: Record<string, SystemSummary[]> = {};
    systems.forEach(sys => {
      const area = sys.functionalArea || 'Other';
      if (!grouped[area]) grouped[area] = [];
      grouped[area].push(sys);
    });
    return grouped;
  }, [systems]);

  if (canvases.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
        <Database className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Data Canvases</h3>
        <p className="text-sm text-gray-500">
          Generate data canvases for your strategic questions to see the cross-system overview.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-5 gap-4">
        <StatCard
          icon={<Database className="w-5 h-5 text-blue-500" />}
          label="Systems"
          value={stats.totalSystems}
          sublabel={`${stats.primarySystems} primary`}
        />
        <StatCard
          icon={<Link2 className="w-5 h-5 text-purple-500" />}
          label="Connections"
          value={stats.totalConnections}
          sublabel="cross-system"
        />
        <StatCard
          icon={<Table2 className="w-5 h-5 text-emerald-500" />}
          label="Questions"
          value={stats.totalCanvases}
          sublabel="with mappings"
        />
        <StatCard
          icon={<CheckCircle2 className="w-5 h-5 text-green-500" />}
          label="Approved"
          value={stats.approvedCanvases}
          sublabel={`of ${stats.totalCanvases}`}
        />
        <StatCard
          icon={<Clock className="w-5 h-5 text-amber-500" />}
          label="Pending"
          value={stats.totalCanvases - stats.approvedCanvases}
          sublabel="to review"
        />
      </div>

      {/* Systems by Functional Area */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <h3 className="font-medium text-gray-900">Systems by Functional Area</h3>
          <p className="text-xs text-gray-500 mt-1">
            Click a system to see which questions it supports
          </p>
        </div>

        <div className="p-4 space-y-4">
          {Object.entries(systemsByArea).map(([area, areaSystems]) => (
            <div key={area}>
              <div className="text-sm font-medium text-gray-700 mb-2">{area}</div>
              <div className="grid grid-cols-2 gap-3">
                {areaSystems.map(sys => (
                  <SystemCard
                    key={sys.systemId}
                    system={sys}
                    onClick={() => onSystemClick?.(sys.systemId)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Cross-System Connections */}
      {connections.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <h3 className="font-medium text-gray-900">Cross-System Data Flows</h3>
            <p className="text-xs text-gray-500 mt-1">
              Shows how data connects between different enterprise systems
            </p>
          </div>

          <div className="p-4 space-y-2">
            {connections.map((conn, idx) => {
              const sourceSystem = systems.find(s => s.systemId === conn.sourceSystemId);
              const targetSystem = systems.find(s => s.systemId === conn.targetSystemId);

              return (
                <div
                  key={idx}
                  className="flex items-center gap-3 p-2 rounded hover:bg-gray-50"
                >
                  <div className="flex-1 text-sm">
                    <span className="font-medium text-blue-700">
                      {sourceSystem?.systemName || conn.sourceSystemId}
                    </span>
                    {sourceSystem?.functionalArea && (
                      <span className="text-gray-400 text-xs ml-1">
                        ({sourceSystem.functionalArea})
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-2 px-3">
                    <div className="border-t border-dashed border-gray-300 w-6" />
                    <Badge variant="outline" className="text-xs">
                      {conn.connectionCount} link{conn.connectionCount !== 1 ? 's' : ''}
                    </Badge>
                    <ArrowRight className="w-4 h-4 text-gray-400" />
                    <div className="border-t border-dashed border-gray-300 w-6" />
                  </div>

                  <div className="flex-1 text-sm text-right">
                    <span className="font-medium text-purple-700">
                      {targetSystem?.systemName || conn.targetSystemId}
                    </span>
                    {targetSystem?.functionalArea && (
                      <span className="text-gray-400 text-xs ml-1">
                        ({targetSystem.functionalArea})
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Questions List */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <h3 className="font-medium text-gray-900">Question Mappings</h3>
        </div>

        <div className="divide-y divide-gray-100">
          {canvases.map(canvas => (
            <div
              key={canvas.canvas_id}
              className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
              onClick={() => onCanvasClick?.(canvas.canvas_id)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900 truncate">
                    {canvas.question_text}
                  </p>
                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <Database className="w-3 h-3" />
                      {canvas.total_systems} systems
                    </span>
                    <span className="flex items-center gap-1">
                      <Link2 className="w-3 h-3" />
                      {canvas.total_connections} connections
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {canvas.approved ? (
                    <Badge variant="success">Approved</Badge>
                  ) : (
                    <Badge variant="outline">Draft</Badge>
                  )}
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Sub-component: Stat Card
interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: number;
  sublabel: string;
}

const StatCard: React.FC<StatCardProps> = ({ icon, label, value, sublabel }) => (
  <div className="bg-white rounded-lg border border-gray-200 p-4">
    <div className="flex items-center gap-3">
      <div className="p-2 bg-gray-50 rounded-lg">{icon}</div>
      <div>
        <div className="text-2xl font-bold text-gray-900">{value}</div>
        <div className="text-xs text-gray-500">{label}</div>
        <div className="text-xs text-gray-400">{sublabel}</div>
      </div>
    </div>
  </div>
);

// Sub-component: System Card
interface SystemCardProps {
  system: SystemSummary;
  onClick?: () => void;
}

const SystemCard: React.FC<SystemCardProps> = ({ system, onClick }) => (
  <div
    className="p-3 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50/30 cursor-pointer transition-all"
    onClick={onClick}
  >
    <div className="flex items-start justify-between mb-2">
      <div>
        <div className="font-medium text-sm text-gray-900">{system.systemName}</div>
        <div className="text-xs text-gray-500">{system.vendor}</div>
      </div>
      <div className="flex gap-1">
        {Array.from(system.roles).map(role => (
          <span key={role}>{getRoleIcon(role)}</span>
        ))}
      </div>
    </div>

    <div className="flex items-center gap-3 text-xs text-gray-500">
      <span className="flex items-center gap-1">
        <Table2 className="w-3 h-3" />
        {system.questionCount} question{system.questionCount !== 1 ? 's' : ''}
      </span>
      <span className="flex items-center gap-1">
        <Columns3 className="w-3 h-3" />
        {system.tableCount} table{system.tableCount !== 1 ? 's' : ''}
      </span>
      {system.connectionCount > 0 && (
        <span className="flex items-center gap-1 text-blue-600">
          <Link2 className="w-3 h-3" />
          {system.connectionCount}
        </span>
      )}
    </div>

    <div className="flex gap-1 mt-2">
      {Array.from(system.roles).map(role => (
        <span key={role}>{getRoleBadge(role)}</span>
      ))}
    </div>
  </div>
);

export default CrossSystemOverview;
